"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.STAGE_OWNER_VALUES = exports.journeyIdFor = exports.JourneyRecordSchema = exports.JOURNEY_SUBJECT_TYPES = exports.StageCompletionSchema = exports.JourneyEventSchema = exports.JOURNEY_AUDIENCES = exports.JOURNEY_EVENT_KINDS = exports.ActorSchema = void 0;
exports.activeCompletions = activeCompletions;
exports.manualFromRecord = manualFromRecord;
exports.visibleEvents = visibleEvents;
const zod_1 = require("zod");
const orderJourney_1 = require("./orderJourney");
/**
 * THE JOURNEY RECORD — what is PERSISTED about a journey, as opposed to what
 * is derived from evidence.
 *
 * The split matters. `resolveJourney` reads facts other services already own
 * (an executed envelope, a keyed terminal, a paid invoice) and needs nothing
 * stored. What genuinely has to be kept is what no other service knows:
 *
 *   · which stage list this journey started with, so adding a stage later
 *     cannot retroactively grow a blocker on work already in flight;
 *   · manual completions — a person deciding, with WHO and WHEN;
 *   · the timeline, append-only, which outlives fulfilment by years.
 */
/** Who acted. Snapshotted at the moment, never joined later. */
exports.ActorSchema = zod_1.z.object({
    kind: zod_1.z.enum(['staff', 'partner', 'tenant', 'system']),
    /** Cognito sub for staff — the stable id that survives a name change. */
    id: zod_1.z.string().max(120).optional(),
    /**
     * Their name AS IT WAS. Deliberately a snapshot: joining to the staff record
     * on read means an employee who leaves, marries or changes role silently
     * rewrites two-year-old history. The id is kept so their current record can
     * still be found when someone actually wants it.
     */
    name: zod_1.z.string().min(1).max(160),
    email: zod_1.z.string().max(160).optional(),
    /** Their role at the time, from the team management system. */
    roleId: zod_1.z.string().max(120).optional(),
    roleName: zod_1.z.string().max(120).optional(),
});
exports.JOURNEY_EVENT_KINDS = [
    /** A stage moved. `stageId` and `to` say which and where. */
    'stage',
    /** Somebody said something. Audience-tagged, like the merchant thread. */
    'message',
    /** A note nobody outside RubyTill ever reads. */
    'note',
    /** Something happened elsewhere worth recording here — a payment, a claim. */
    'external',
];
exports.JOURNEY_AUDIENCES = ['rubytill', 'partner', 'tenant'];
/**
 * ONE THING THAT HAPPENED. Append-only, for ever.
 *
 * Never updated and never deleted — a timeline you can edit is not evidence,
 * and this is what a claim or a collection is argued from years later. A
 * correction is a NEW event that says what was corrected.
 */
exports.JourneyEventSchema = zod_1.z.object({
    journeyId: zod_1.z.string().min(1).max(120),
    /** `EVENT#<at>#<rand>` — sortable, and unique when two land in one millisecond. */
    eventId: zod_1.z.string().min(1).max(80),
    at: zod_1.z.number(),
    kind: zod_1.z.enum(exports.JOURNEY_EVENT_KINDS),
    actor: exports.ActorSchema,
    /** Which stage this concerns, when it concerns one. */
    stageId: zod_1.z.string().max(80).optional(),
    /** Where the stage went. */
    to: zod_1.z.string().max(40).optional(),
    /** One line, written for a human reading the timeline cold in two years. */
    summary: zod_1.z.string().min(1).max(500),
    body: zod_1.z.string().max(4000).optional(),
    /** Who may read a message. Empty = RubyTill only. */
    audience: zod_1.z.array(zod_1.z.enum(exports.JOURNEY_AUDIENCES)).max(3).default([]),
    /**
     * WHAT this is about, so the timeline can be read by subject rather than by
     * journey: a unit's whole history, a model's, a location's. Free-form ids —
     * `unit:u_1`, `model:mdl_k10`, `invoice:in_1`.
     */
    subjects: zod_1.z.array(zod_1.z.string().max(120)).max(20).default([]),
    /** Denormalised for the by-tenant view. Never read to make a decision. */
    tenantId: zod_1.z.string().max(120).optional(),
});
/** A person completing a stage the system cannot observe. */
exports.StageCompletionSchema = zod_1.z.object({
    stageId: zod_1.z.string().min(1).max(80),
    at: zod_1.z.number(),
    actor: exports.ActorSchema,
    /** Why, when the stage asks for it — a verification note, a reference. */
    note: zod_1.z.string().max(1000).optional(),
    /**
     * The proof, when the stage is completed BY uploading it — a photo of the
     * test sale's receipt. An S3 key in the shared uploads bucket, never a URL:
     * it is opened through a short-lived presigned link.
     */
    attachment: zod_1.z.object({
        key: zod_1.z.string().min(1).max(500),
        name: zod_1.z.string().max(200).optional(),
        contentType: zod_1.z.string().max(100).optional(),
    }).optional(),
    /**
     * A person completing a stage that normally completes from EVIDENCE — the
     * agreement with no envelope, a reader keyed somewhere we cannot see. Always
     * carries a `note` (the server refuses one without), and the rail draws it
     * as a manual completion, never as the evidence having arrived.
     */
    override: zod_1.z.boolean().optional(),
    /** Undone by a later record, never erased. See `activeCompletions`. */
    undoneAt: zod_1.z.number().optional(),
    undoneBy: exports.ActorSchema.optional(),
});
exports.JOURNEY_SUBJECT_TYPES = ['order', 'location', 'tenant'];
exports.JourneyRecordSchema = zod_1.z.object({
    journeyId: zod_1.z.string().min(1).max(120),
    tenantId: zod_1.z.string().min(1).max(120),
    /** What this journey is about — `order:RTA-1`, `location:loc_1`. */
    subjectType: zod_1.z.enum(exports.JOURNEY_SUBJECT_TYPES),
    subjectId: zod_1.z.string().min(1).max(120),
    locationId: zod_1.z.string().max(120).optional(),
    /** Which template, and which VERSION of it, this journey started with. */
    templateId: zod_1.z.string().min(1).max(80),
    templateVersion: zod_1.z.number().int().min(1),
    /**
     * THE SNAPSHOT. The stage ids this journey started with, in order.
     *
     * A new stage added to the template does NOT appear here. Adding a blocking
     * stage that silently applied to work already in flight would stop shipments
     * nobody knew were stopping; a migration adds it here deliberately, and the
     * screen offering that migration says how many journeys it would block.
     */
    stageIds: zod_1.z.array(zod_1.z.string().max(80)).max(100),
    completions: zod_1.z.array(exports.StageCompletionSchema).max(200).default([]),
    createdAt: zod_1.z.number(),
    updatedAt: zod_1.z.number().optional(),
    closedAt: zod_1.z.number().optional(),
});
/** `order:ord_123` → `jny_order_ord_123`. Derived, so one subject has one journey. */
const journeyIdFor = (subjectType, subjectId) => `jny_${subjectType}_${subjectId}`;
exports.journeyIdFor = journeyIdFor;
/**
 * The completions that still stand, newest per stage, ignoring undone ones.
 *
 * An undo is a NEW record rather than a deletion, so "Aruna verified this,
 * then Priya un-verified it an hour later" survives — which is exactly the
 * sequence somebody will ask about.
 */
function activeCompletions(record) {
    const out = {};
    for (const c of record?.completions || []) {
        if (c.undoneAt) {
            delete out[c.stageId];
            continue;
        }
        const prev = out[c.stageId];
        if (!prev || c.at >= prev.at)
            out[c.stageId] = c;
    }
    return out;
}
/** Shaped the way `resolveJourney`'s `manual` field wants it. */
function manualFromRecord(record) {
    const active = activeCompletions(record);
    const out = {};
    for (const [stageId, c] of Object.entries(active)) {
        out[stageId] = {
            name: c.actor.name, at: c.at, staffId: c.actor.id, roleId: c.actor.roleId,
            ...(c.attachment ? { attachment: c.attachment } : {}),
            ...(c.override ? { override: true, note: c.note } : {}),
        };
    }
    return out;
}
/** Which timeline events one reader may see. Mirrors the merchant thread's rule. */
function visibleEvents(events, viewer) {
    if (viewer === 'rubytill')
        return [...events];
    return events.filter((e) => (e.audience || []).includes(viewer));
}
exports.STAGE_OWNER_VALUES = orderJourney_1.STAGE_OWNERS;
//# sourceMappingURL=journeyRecord.js.map